import React from 'react';
import styled from 'styled-components';
import { Flex, Tabs, Tab, Tag } from '@decisiv/ui-components';
import { AdminPage, AdminPanel } from 'lib/admin-kit';
import PatchedTable from 'components/PatchedTable';

const READINGS_ENDPOINT = '/internal_admin/admin_kit/diagnostic_readings';

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 24px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-size: 14px;
  color: #1a202c;
`;

const renderText = (value) => (value == null || value === '' ? '—' : String(value));

const SENSOR_COLUMNS = (labels) => [
  { name: 'name', title: labels.name, DataCell: ({ rowData }) => <span>{renderText(rowData.name)}</span> },
  { name: 'value', title: labels.value, DataCell: ({ rowData }) => <span>{renderText(rowData.value)}</span> },
  { name: 'unit_of_measure', title: labels.unitOfMeasure, DataCell: ({ rowData }) => <span>{renderText(rowData.unit_of_measure)}</span> },
];

const GUIDANCE_COLUMNS = (labels) => [
  { name: 'name', title: labels.name, DataCell: ({ rowData }) => <span>{renderText(rowData.name)}</span> },
  { name: 'description', title: labels.description, DataCell: ({ rowData }) => <span>{renderText(rowData.description)}</span> },
  { name: 'value', title: labels.value, DataCell: ({ rowData }) => <span>{renderText(rowData.value)}</span> },
];

const Show = ({ record, labels = {} }) => {
  const summaryLeft = [
    [labels.faultCode, record.fault_code],
    ['SPN', record.spn],
    ['FMI', record.fmi],
    [labels.severity, record.severity],
    [labels.severityLevel, record.severity_level],
  ];
  const summaryRight = [
    [labels.triggeredAt, record.triggered_at],
    [labels.component, record.component],
    [labels.description, record.description],
    [labels.occurrences, record.occurrences],
    [labels.odometer, record.odometer],
  ];

  const tabsConfig = [];
  if (record.sensor_readings?.length) {
    tabsConfig.push({
      id: 'sensor_readings',
      title: labels.sensorReadings,
      body: <PatchedTable columns={SENSOR_COLUMNS(labels)} data={record.sensor_readings} getRowKey={(_, i) => i} />,
    });
  }
  if (record.location) {
    tabsConfig.push({
      id: 'location',
      title: labels.location,
      body: (
        <SummaryGrid>
          <div><Label>{labels.latitude}</Label><Value>{renderText(record.location?.latitude)}</Value></div>
          <div><Label>{labels.longitude}</Label><Value>{renderText(record.location?.longitude)}</Value></div>
        </SummaryGrid>
      ),
    });
  }
  if (record.guidance?.length) {
    tabsConfig.push({
      id: 'guidance',
      title: labels.guidance,
      body: <PatchedTable columns={GUIDANCE_COLUMNS(labels)} data={record.guidance} getRowKey={(_, i) => i} />,
    });
  }

  return (
    <AdminPage
      title={labels.faultDetail}
      breadcrumbs={[
        { text: labels.diagnosticReadings, href: READINGS_ENDPOINT },
        { text: labels.readingDetail, href: `${READINGS_ENDPOINT}/${record.diagnostic_reading_id}` },
        { text: labels.faultDetail },
      ]}
      actions={
        <Flex>
          {record.active
            ? <Tag intent="danger" text={labels.active} />
            : <Tag intent="neutral" text={labels.inactive} />}
        </Flex>
      }
    >
      <AdminPanel title={labels.faultDetail}>
        <SummaryGrid>
          <div>
            {summaryLeft.map(([k, v]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <Label>{k}</Label>
                <Value>{renderText(v)}</Value>
              </div>
            ))}
          </div>
          <div>
            {summaryRight.map(([k, v]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <Label>{k}</Label>
                <Value>{renderText(v)}</Value>
              </div>
            ))}
          </div>
        </SummaryGrid>
      </AdminPanel>

      {tabsConfig.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Tabs defaultSelectedId={tabsConfig[0].id}>
            {tabsConfig.map((tab) => (
              <Tab key={tab.id} id={tab.id} title={tab.title}>
                <div style={{ marginTop: 16 }}>{tab.body}</div>
              </Tab>
            ))}
          </Tabs>
        </div>
      )}
    </AdminPage>
  );
};

export default Show;
